import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select } from '@react-apps/design-system';
import type {
  TotvsProductLink,
  TotvsProductLinkAction,
  TotvsProvider,
} from '../../domain/entities/totvs.entity';
import { TOTVS_PROVIDERS } from '../../domain/entities/totvs.entity';
import {
  useCreateTotvsProductLink,
  useUpdateTotvsProductLink,
} from '../hooks/use-totvs-product-links';
import { useProviderAccounts } from '@/modules/sales/presentation/hooks/use-provider-accounts';
import type { ProviderAccount } from '@/modules/sales/domain/entities/sales.entity';
import { tenantLabel } from '@/modules/sales/presentation/shared/display';
import {
  ErrorText,
  Field,
  getApiErrorMessage,
  getTotvsProviderLabel,
} from '../display';

const PROVIDER_OPTIONS = TOTVS_PROVIDERS.map((provider) => ({
  value: provider,
  label: getTotvsProviderLabel(provider),
}));

// Não existe mais opção de curinga: o de-para pertence a uma conta do gateway,
// e o `''` aqui é só o estado "ainda não escolheu" do próprio select.
const NO_ACCOUNT_SELECTED = '';
const NOTES_MAX_LENGTH = 40;

const ACTION_OPTIONS: Array<{ value: TotvsProductLinkAction; label: string }> =
  [
    { value: 'enroll', label: 'Matricular no RM' },
    { value: 'ignore', label: 'Ignorar (não matricular)' },
  ];

function getProviderAccountOptionLabel(account: ProviderAccount): string {
  if (!account.notes) return account.displayName;
  const notes =
    account.notes.length > NOTES_MAX_LENGTH
      ? `${account.notes.slice(0, NOTES_MAX_LENGTH - 1)}…`
      : account.notes;
  return `${account.displayName} — ${notes}`;
}

interface TotvsProductLinkFormModalProps {
  open: boolean;
  onClose: () => void;
  link?: TotvsProductLink | null;
}

export function TotvsProductLinkFormModal({
  open,
  onClose,
  link,
}: TotvsProductLinkFormModalProps) {
  const isEdit = !!link;
  const create = useCreateTotvsProductLink();
  const update = useUpdateTotvsProductLink();
  const mutation = isEdit ? update : create;

  const [provider, setProvider] = useState<TotvsProvider>('guru');
  const [providerAccountId, setProviderAccountId] = useState('');
  const [productExternalId, setProductExternalId] = useState('');
  const [offerExternalId, setOfferExternalId] = useState('');
  const [action, setAction] = useState<TotvsProductLinkAction>('enroll');
  const [ignoreReason, setIgnoreReason] = useState('');
  const [idAreaInteresse, setIdAreaInteresse] = useState('');
  const [codColigada, setCodColigada] = useState('1');
  const [codFilial, setCodFilial] = useState('1');
  const [idPs, setIdPs] = useState('1');
  const [enabled, setEnabled] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const { data: providerAccounts, error: providerAccountsError } =
    useProviderAccounts(provider);
  const providerAccountOptions = useMemo(
    () => [
      { value: NO_ACCOUNT_SELECTED, label: 'Selecione a conta…' },
      ...(providerAccounts?.map((account) => ({
        value: account.id,
        label: getProviderAccountOptionLabel(account),
      })) ?? []),
    ],
    [providerAccounts],
  );
  const selectedProviderAccount = providerAccounts?.find(
    (account) => account.id === providerAccountId,
  );

  useEffect(() => {
    if (!open) return;
    setProvider(link?.provider ?? 'guru');
    setProviderAccountId(link?.providerAccountId ?? '');
    setProductExternalId(link?.productExternalId ?? '');
    setOfferExternalId(link?.offerExternalId ?? '');
    setAction(link?.action ?? 'enroll');
    setIgnoreReason(link?.ignoreReason ?? '');
    setIdAreaInteresse(link?.idAreaInteresse ?? '');
    setCodColigada(String(link?.codColigada ?? 1));
    setCodFilial(String(link?.codFilial ?? 1));
    setIdPs(link?.idPs ?? '1');
    setEnabled(link?.enabled ?? true);
    setLocalError(null);
  }, [open, link]);

  const close = () => {
    mutation.reset();
    setLocalError(null);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    const product = productExternalId.trim();
    const offer = offerExternalId.trim();
    if (!product || !offer) {
      setLocalError('Informe o ID externo do produto e o da oferta.');
      return;
    }

    const account = providerAccountId.trim();
    if (!account) {
      setLocalError('Escolha a conta do gateway a que este de-para pertence.');
      return;
    }

    // Destino `ignore`: nada do RM viaja, e os campos vão explicitamente como
    // `null` para que a edição limpe o que o registro tinha antes — o back
    // recusa a combinação meio-a-meio.
    if (action === 'ignore') {
      const reason = ignoreReason.trim();
      if (!reason) {
        setLocalError('Diga por que esta venda não deve virar matrícula.');
        return;
      }

      const ignorePayload = {
        provider,
        providerAccountId: account,
        productExternalId: product,
        offerExternalId: offer,
        action,
        ignoreReason: reason,
        idAreaInteresse: null,
        codColigada: null,
        codFilial: null,
        idPs: null,
        enabled,
      };

      if (isEdit) {
        update.mutate(
          { id: link.id, input: ignorePayload },
          { onSuccess: close },
        );
      } else {
        create.mutate(ignorePayload, { onSuccess: close });
      }
      return;
    }

    const parsedCodColigada = Number(codColigada);
    const parsedCodFilial = Number(codFilial);
    if (!Number.isInteger(parsedCodColigada) || parsedCodColigada < 1) {
      setLocalError('CODCOLIGADA deve ser um inteiro positivo.');
      return;
    }
    if (!Number.isInteger(parsedCodFilial) || parsedCodFilial < 1) {
      setLocalError('CODFILIAL deve ser um inteiro positivo.');
      return;
    }

    const payload = {
      provider,
      providerAccountId: account,
      productExternalId: product,
      offerExternalId: offer,
      action,
      ignoreReason: null,
      idAreaInteresse: idAreaInteresse.trim(),
      codColigada: parsedCodColigada,
      codFilial: parsedCodFilial,
      idPs: idPs.trim() || '1',
      enabled,
    };

    if (!payload.idAreaInteresse) {
      setLocalError('IDAREAINTERESSE é obrigatório.');
      return;
    }

    if (isEdit) {
      update.mutate({ id: link.id, input: payload }, { onSuccess: close });
    } else {
      create.mutate(payload, { onSuccess: close });
    }
  };

  const canSubmit = !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={close}
      title={isEdit ? 'Editar de-para TOTVS' : 'Criar de-para TOTVS'}
      description="Decide o destino de cada produto/oferta do Guru/Hotmart: matricular no RM (com os campos da fórmula visual) ou ignorar com justificativa."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Gateway"
          options={PROVIDER_OPTIONS}
          value={provider}
          onChange={(e) => {
            setProvider(e.target.value as TotvsProvider);
            setProviderAccountId(NO_ACCOUNT_SELECTED);
          }}
          helperText="Escopa a unicidade e indica onde conferir o produto/oferta."
        />

        <Select
          label="Conta do gateway"
          options={providerAccountOptions}
          value={providerAccountId}
          onChange={(e) => setProviderAccountId(e.target.value)}
          required
          // Sem a lista o select obrigatório trava o salvar; o erro diz por
          // quê em vez de deixar só o placeholder.
          error={
            providerAccountsError
              ? `Não foi possível carregar as contas do gateway: ${getApiErrorMessage(providerAccountsError, 'erro desconhecido')}`
              : undefined
          }
          helperText="O de-para só casa eventos desta conta — o mesmo produto/oferta vindo de outra conta não casa, mesmo com [TV]."
        />

        {selectedProviderAccount && (
          <div className="border-(--color-border) bg-(--color-bg-secondary) space-y-3 rounded-lg border p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Gateway"
                value={getTotvsProviderLabel(selectedProviderAccount.provider)}
              />
              <Field
                label="Unidade (BU)"
                value={tenantLabel(selectedProviderAccount.tenant)}
              />
              <Field label="Nome" value={selectedProviderAccount.displayName} />
            </div>
            <Field label="Notas" value={selectedProviderAccount.notes} />
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="ID produto externo"
            value={productExternalId}
            onChange={(e) => setProductExternalId(e.target.value)}
            placeholder="ex.: product_123"
            required
          />
          <Input
            label="ID oferta externa"
            value={offerExternalId}
            onChange={(e) => setOfferExternalId(e.target.value)}
            placeholder="ex.: offer_456"
            required
          />
        </div>

        <Select
          label="Destino"
          options={ACTION_OPTIONS}
          value={action}
          onChange={(e) => setAction(e.target.value as TotvsProductLinkAction)}
          helperText="Ignorar descarta o job com o motivo registrado, sem tocar no RM — é o destino do produto que saiu de linha e continua chegando por recorrência."
        />

        {action === 'ignore' ? (
          <Input
            label="Por que ignorar"
            value={ignoreReason}
            onChange={(e) => setIgnoreReason(e.target.value)}
            placeholder="ex.: produto descontinuado em 07/2026, sem turma no RM"
            required
          />
        ) : (
          <>
            <Input
              label="IDAREAINTERESSE"
              value={idAreaInteresse}
              onChange={(e) => setIdAreaInteresse(e.target.value)}
              required
            />

            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="CODCOLIGADA"
                type="number"
                min="1"
                value={codColigada}
                onChange={(e) => setCodColigada(e.target.value)}
                required
              />
              <Input
                label="CODFILIAL"
                type="number"
                min="1"
                value={codFilial}
                onChange={(e) => setCodFilial(e.target.value)}
                required
              />
              <Input
                label="IDPS"
                value={idPs}
                onChange={(e) => setIdPs(e.target.value)}
                required
              />
            </div>
          </>
        )}

        <label className="text-(--color-text-secondary) flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Ativo para resolver novos contratos [TV]
        </label>

        {(localError || mutation.error) && (
          <ErrorText>
            {localError ??
              getApiErrorMessage(
                mutation.error,
                isEdit ? 'Falha ao editar de-para' : 'Falha ao criar de-para',
              )}
          </ErrorText>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {mutation.isPending ? 'Salvando…' : isEdit ? 'Salvar' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
